import { Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * What the name field starts as — a generated name the reader can accept with
   * Enter. Never empty: a prompt with a blank box is the validation error this
   * dialog exists to replace, moved one step earlier.
   */
  suggestion: string;
  /**
   * What the description field starts as: the description the draft already
   * carries, and NOTHING otherwise.
   *
   * Never generated. A description prefilled with a machine sentence would be
   * accepted unread by nearly everybody, leaving every report "described" and
   * none of them described. The field asks; it does not answer. (The builder
   * used to print a derived sentence under the report's title and this comment
   * leaned on it; that line is gone, and the reasoning never depended on it.)
   */
  description: string;
  /**
   * The dialog's own title and blurb.
   *
   * Optional because the SAVE prompt is what this dialog was built for and its
   * words are the default. The pencil on the report's heading opens the same
   * two fields for a report that is already named, where "Name this report" is
   * the wrong sentence.
   */
  heading?: string;
  blurb?: string;
  /**
   * What is being named — the word the field labels are built from. A
   * dashboard is named and described the way a report is (the same two
   * columns), so it borrows this dialog rather than growing a second copy that
   * would drift from it.
   */
  noun?: 'report' | 'dashboard';
  /**
   * The server's limits for THIS kind of thing, passed in rather than imported:
   * reports and dashboards validate separately, and a shared constant here
   * would silently clamp one to the other's rule the day they differ.
   */
  maxName: number;
  maxDescription: number;
  /** Shown under the field, and the reason the dialog stays open on a refusal. */
  error: string | null;
  busy: boolean;
  onSubmit: (name: string, description: string) => void;
  submitLabel: string;
}

/**
 * Name it, describe it if you like, then save it.
 *
 * Built for saving a REPORT, which is what the reasoning below is about; a
 * dashboard uses it only from the pencil on its heading, to rename one that
 * already exists.
 *
 * WHY A PROMPT AT ALL, when both are editable in place on the report itself:
 * because nothing made anybody use that. Save was live on an unnamed draft, the
 * server refused it (`name` has a `min(1)`), and the refusal arrived as a toast
 * reading "Validation failed" — the field's own message never left the
 * envelope. A save that cannot succeed should not be a round trip, and the
 * repair for a missing name is a box to type one in.
 *
 * The name is PREFILLED and selected, so the common answer is Enter and the
 * next-commonest is to type over it. It is a decision the reader is allowed to
 * make, not a form they are made to fill in.
 *
 * The DESCRIPTION is optional and starts empty unless the draft already has
 * one. A report with none is a legible state everywhere it appears — the list
 * card says "No description.", the report's own page falls back to a sentence
 * about who may edit it — so leaving the box alone costs nothing, and there is
 * no second visit needed to fill it in later: the same text is editable in
 * place under the report's title.
 *
 * It stays open on failure and shows the message inline. A name collision is
 * the likeliest refusal, and it is answered by editing the name — which is
 * exactly what is on screen, so closing the dialog to re-open it would be
 * throwing away the reader's place.
 */
export function NameDescriptionDialog({
  open,
  onOpenChange,
  suggestion,
  description: initialDescription,
  noun = 'report',
  maxName,
  maxDescription,
  heading = `Name this ${noun}`,
  blurb = 'This is how you’ll find it again, and what anyone you share it with will see.',
  error,
  busy,
  onSubmit,
  submitLabel,
}: Props) {
  const [name, setName] = useState(suggestion);
  const [description, setDescription] = useState(initialDescription);
  const input = useRef<HTMLInputElement>(null);

  // Re-seeded when the dialog OPENS, and on nothing else — both values are
  // derived from a draft that keeps changing underneath, and a prop that
  // re-seeded on every change would overwrite half-typed text whenever the
  // preview refetched.
  const seed = useRef({ suggestion, description: initialDescription });
  seed.current = { suggestion, description: initialDescription };
  useEffect(() => {
    if (!open) return;
    setName(seed.current.suggestion);
    setDescription(seed.current.description);
    // Selected, not just focused: the suggestion is a starting point, so typing
    // replaces it and Enter accepts it. Deferred a frame because Radix moves
    // focus to the dialog itself as it opens.
    const id = requestAnimationFrame(() => input.current?.select());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const trimmed = name.trim();
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!trimmed || busy) return;
            onSubmit(trimmed, description.trim());
          }}
        >
          <DialogHeader>
            <DialogTitle>{heading}</DialogTitle>
            <DialogDescription>{blurb}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor={`${noun}-name`}>{Noun} name</Label>
              <Input
                id={`${noun}-name`}
                ref={input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={maxName}
                autoComplete="off"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${noun}-name-error` : undefined}
              />
              {error ? (
                <p id={`${noun}-name-error`} role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              {/* Marked OPTIONAL on the label rather than left to be inferred:
                  an unmarked second box in a save dialog reads as another thing
                  standing between the reader and their report. */}
              <Label htmlFor={`${noun}-description`}>
                Description <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id={`${noun}-description`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={maxDescription}
                rows={3}
                placeholder={`What this ${noun} answers, and who it’s for.`}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || busy}>
              {busy ? 'Saving…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The pencil that opens this dialog on a thing that is already named — the
 * `titleAction` beside a report's or a dashboard's heading. One component so
 * the two headers cannot drift apart in look or label (e2e selects by it).
 */
export function EditDetailsButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8 shrink-0 text-muted-foreground"
      aria-label="Edit name and description"
      title="Edit name and description"
      onClick={onClick}
    >
      <Pencil className="size-4" />
    </Button>
  );
}
